import { useMemo } from 'react';
import { compareAsc, compareDesc } from 'date-fns';

import { Query } from '../../design-system/types';
import { DisplayDossierOverviews, DossierOverview, NormalizedDossier } from '../dossier/types';
import { useGetDossierOverviews } from '../dossier-overview/hooks';
import { useAuth } from '../../shared/providers/AuthProvider';
import { formatDossierAndFilterByQuery } from '../todo/operations';

const sortByLastTravellerToAgentMessageDate = (
    dossierOverviews: DisplayDossierOverviews,
): DisplayDossierOverviews =>
    dossierOverviews.sort(
        (a, b) =>
            (a.discussion.lastTravellerToAgentMessageDate &&
                b.discussion.lastTravellerToAgentMessageDate &&
                compareDesc(
                    a.discussion.lastTravellerToAgentMessageDate,
                    b.discussion.lastTravellerToAgentMessageDate,
                )) ||
            0,
    );

const sortByRequestDate = (dossierOverviews: DisplayDossierOverviews): DisplayDossierOverviews =>
    dossierOverviews.sort(
        (a, b) => a.requestDate && b.requestDate && compareAsc(a.requestDate, b.requestDate),
    );

export const useGetResponseRequiredDiscussions = (query: Query) => {
    const { data: dossiers, isSuccess, isLoading } = useGetDossierOverviews();
    const { userId } = useAuth();

    const data = useMemo(() => {
        if (!dossiers) return [];

        const dossiersWithResponseRequiredDiscussions = Object.values(dossiers)
            .filter((dossier) => dossier.discussion.hasResponseRequired)
            .reduce((dossier: NormalizedDossier, key: DossierOverview) => {
                dossier[key.id] = key;
                return dossier;
            }, {});

        const formattedDossiers = formatDossierAndFilterByQuery(
            query,
            userId,
        )(dossiersWithResponseRequiredDiscussions);
        return sortByLastTravellerToAgentMessageDate(formattedDossiers);
    }, [dossiers, query, userId]);

    return { data, isSuccess, isLoading };
};

export const useGetNewRequestDiscussions = (query: Query) => {
    const { data: dossiers, isSuccess, isLoading } = useGetDossierOverviews();
    const { userId } = useAuth();

    const data = useMemo(() => {
        if (!dossiers) return [];
        const dossiersWithNewRequestDiscussions = Object.values(dossiers)
            .filter(
                (dossier) =>
                    dossier.step === 'new_request' &&
                    (dossier.discussion.lastAgentToTravellerMessageDate === null ||
                        (dossier.project.maturityExperiments?.isLatencyTest &&
                            dossier.project.maturityExperiments?.at1MessageDate ===
                                dossier.discussion.lastAgentToTravellerMessageDate)),
            )
            .reduce((dossier: NormalizedDossier, key: DossierOverview) => {
                dossier[key.id] = key;
                return dossier;
            }, {});

        const formattedDossiers = formatDossierAndFilterByQuery(
            query,
            userId,
        )(dossiersWithNewRequestDiscussions);
        return sortByRequestDate(formattedDossiers);
    }, [dossiers, query, userId]);

    return { data, isSuccess, isLoading };
};

export const useGetInboxCount = (query: Query) => {
    const { data: responseRequiredDiscussions } = useGetResponseRequiredDiscussions(query);
    const { data: newRequestDiscussions } = useGetNewRequestDiscussions(query);

    return useMemo(
        () => responseRequiredDiscussions.length + newRequestDiscussions.length,
        [responseRequiredDiscussions.length, newRequestDiscussions.length],
    );
};
